import React, { useEffect, useRef } from "react";

import { useTheme } from "@mui/material/styles";

import * as echarts from "echarts";

import item_primary_icn from "assets/images/icons/item_primary.svg";
import item_blue_light_icn from "assets/images/icons/item_blue_light.svg";
import item_sliver_icn from "assets/images/icons/item_sliver.svg";

// 樣式表
const iconStyleSheet = {
  main: item_primary_icn,
  blueSecound: item_blue_light_icn,
  greyThird: item_sliver_icn,
}

const barStyleSheet = (theme) => ({
  main: {
    itemStyle: { color: theme.palette.primary.main },
  },
  blueSecound: {
    itemStyle: { color: theme.palette.primary.blueSecound },
  },
  greyThird: {
    itemStyle: { color: theme.palette.primary.greyThird },
  },
});

const BarChart = ({ legendStyle, barChartData, height, minInterval }) => {
  const theme = useTheme();

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const { top, right } = legendStyle;
  const styleSheet = barStyleSheet(theme);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    const chart = echarts.init(chartDom, null, {
      renderer: 'svg',
      useDirtyRect: true,
    });
    chartInstance.current = chart;

    const option = {
      legend: {
        top,
        right,
        textStyle: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        },
        data: barChartData.series.map((item) => ({
          name: item.name,
          icon: `image://${iconStyleSheet[item.type]}`
        })),
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.greyPrimaryTransparency,
        textStyle: { color: theme.palette.primary.white },
        extraCssText: `border-radius: 5px; max-width: 300px;`,
        formatter: (params) => {
          if (!params || params.length === 0) return "";

          const currentTime = new Date(params[0].name);
          const formatTime = (date) => {
            const h = date.getHours().toString().padStart(2, "0");
            const m = date.getMinutes().toString().padStart(2, "0");
            return `${h}:${m}`;
          };

          let tooltipText = `<div style="font-size: 12px;">
                                ${formatTime(currentTime)}
                            </div>`;


          params.forEach(param => {
            const findIcon = barChartData.series.find(item => item.name === param.seriesName);
            const iconSrc = iconStyleSheet[findIcon.type];
            tooltipText += `
                            <div style="display:flex; justify-content: space-between; align-items:center; font-size:13px; color:#FFFFFF;">
                                <span><img src="${iconSrc}" style="width:10px;height:10px;vertical-align:middle;" /> ${param.seriesName}:</span>
                                <span style="font-weight:bold;margin-left:15px;">${Math.abs(param.value)}</span>
                            </div>
                        `;
          });
          return tooltipText;
        },
      },
      grid: {
        left: "8%",
        right: "5%",
        top: "15%",
        bottom: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        axisLabel: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[1],
          formatter: (value) => {
            const date = new Date(value);
            const hours = date.getHours().toString().padStart(2, "0");
            const minutes = date.getMinutes().toString().padStart(2, "0");
            return `${hours}:${minutes}`;
          },
        },
        axisLine: {
          show: true,
          onZero: true,
          lineStyle: {
            color: theme.palette.primary.main,
            width: 1,
          },
        },
        splitLine: {
          show: true,
        },
        data: barChartData.time,
      },
      yAxis: {
        type: "value",
        minInterval: minInterval,
        axisLabel: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[1],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },
      series: barChartData.series.map((item) => ({
        name: item.name,
        type: "bar",
        stack: "all",
        data: item.data,
        itemStyle: styleSheet[item.type].itemStyle,
      }))

    }

    chartInstance.current.setOption(option);

    // resize
    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartDom);

    // clear
    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [barChartData]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height,
        background: theme.palette.primary.white,
      }}
    />
  );
}

export default BarChart